/**
 * Fullscreen product image inspector — tap to open, pinch/scroll zoom, drag to pan.
 */
class ProductGalleryInspect {
  constructor() {
    this.overlay = null;
    this.image = null;
    this.stage = null;
    this.scale = 1;
    this.panX = 0;
    this.panY = 0;
    this.minScale = 1;
    this.maxScale = 6;
    this.zoomLevels = [1];
    for (let percent = 130; percent <= 600; percent += 30) {
      this.zoomLevels.push(percent / 100);
    }
    this.zoomLabel = null;
    this.zoomSlider = null;
    this.pointers = new Map();
    this.lastPinchDistance = 0;
    this.isPanning = false;
    this.panStart = { x: 0, y: 0, panX: 0, panY: 0 };
    this.tapStart = null;
    this.zoomCursor = null;
    this.canUseZoomCursor = window.matchMedia('(pointer: fine)').matches;
    this.clickZoomMoved = false;
    this.clickZoomStart = null;
    this.pendingZoomClick = null;

    this.onKeyDown = this.onKeyDown.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.onStagePointerMoveCursor = this.onStagePointerMoveCursor.bind(this);
    this.onStagePointerEnter = this.onStagePointerEnter.bind(this);
    this.onStagePointerLeave = this.onStagePointerLeave.bind(this);
    this.onStageClickZoom = this.onStageClickZoom.bind(this);
    this.onStageContextMenu = this.onStageContextMenu.bind(this);
    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);

    document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
    document.addEventListener('click', this.onDocumentClick, true);
    document.addEventListener('keydown', this.onDocumentKeyDown, true);
    document.addEventListener('shopify:section:load', () => {});
  }

  getZoomTarget(node) {
    if (!node || typeof node.closest !== 'function') return null;
    const target = node.closest('.product-gallery-zoom-target');
    if (!target || !target.dataset.zoomSrc) return null;
    if (!target.closest('[data-gallery-inspect]')) return null;
    return target;
  }

  onDocumentPointerDown(event) {
    const target = this.getZoomTarget(event.target);
    if (!target) return;
    this.tapStart = { x: event.clientX, y: event.clientY, target };
  }

  onDocumentClick(event) {
    const target = this.getZoomTarget(event.target);
    if (!target) return;

    if (this.tapStart?.target === target) {
      const moved = Math.hypot(event.clientX - this.tapStart.x, event.clientY - this.tapStart.y);
      if (moved > 14) return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.open(target.dataset.zoomSrc, target.dataset.zoomAlt || '');
    this.tapStart = null;
  }

  onDocumentKeyDown(event) {
    const target = this.getZoomTarget(event.target);
    if (!target) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.open(target.dataset.zoomSrc, target.dataset.zoomAlt || '');
  }

  open(src, alt = '') {
    if (!src) return;
    this.close();

    this.overlay = document.createElement('div');
    this.overlay.className = 'product-gallery-inspect';
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute('aria-label', alt || 'Product image');

    this.overlay.innerHTML = `
      <button type="button" class="product-gallery-inspect__backdrop" aria-label="Close"></button>
      <div class="product-gallery-inspect__stage">
        <img class="product-gallery-inspect__image" alt="" draggable="false">
        <div class="product-gallery-inspect__zoom-cursor" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-zoom-in" viewBox="0 0 19 19" aria-hidden="true">
            <path fill="currentColor" fill-rule="evenodd" d="M4.667 7.94a.5.5 0 0 1 .499-.501l5.534-.014a.5.5 0 1 1 .002 1l-5.534.014a.5.5 0 0 1-.5-.5" clip-rule="evenodd"/>
            <path fill="currentColor" fill-rule="evenodd" d="M7.926 4.665a.5.5 0 0 1 .501.498l.014 5.534a.5.5 0 1 1-1 .003l-.014-5.534a.5.5 0 0 1 .499-.501" clip-rule="evenodd"/>
            <path fill="currentColor" fill-rule="evenodd" d="M12.832 3.03a6.931 6.931 0 1 0-9.802 9.802 6.931 6.931 0 0 0 9.802-9.802M2.323 2.323a7.931 7.931 0 0 1 11.296 11.136l4.628 4.628a.5.5 0 0 1-.707.707l-4.662-4.662A7.932 7.932 0 0 1 2.323 2.323" clip-rule="evenodd"/>
          </svg>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-zoom-out" viewBox="0 0 19 19" aria-hidden="true">
            <path fill="currentColor" fill-rule="evenodd" d="M4.667 8.44a.5.5 0 0 1 .499-.501h5.534a.5.5 0 1 1 0 1H5.166a.5.5 0 0 1-.499-.499" clip-rule="evenodd"/>
            <path fill="currentColor" fill-rule="evenodd" d="M12.832 3.03a6.931 6.931 0 1 0-9.802 9.802 6.931 6.931 0 0 0 9.802-9.802M2.323 2.323a7.931 7.931 0 0 1 11.296 11.136l4.628 4.628a.5.5 0 0 1-.707.707l-4.662-4.662A7.932 7.932 0 0 1 2.323 2.323" clip-rule="evenodd"/>
          </svg>
        </div>
      </div>
      <button type="button" class="product-gallery-inspect__close" aria-label="Close">
        <span aria-hidden="true">&times;</span>
      </button>
      <div class="product-gallery-inspect__controls">
        <button type="button" class="product-gallery-inspect__zoom-btn" data-zoom-step="-1" aria-label="Zoom out">−</button>
        <label class="product-gallery-inspect__zoom-label">
          <span class="visually-hidden">Zoom level</span>
          <input
            type="range"
            class="product-gallery-inspect__zoom-slider"
            min="100"
            max="600"
            step="30"
            value="100"
            aria-valuemin="100"
            aria-valuemax="600"
          >
        </label>
        <span class="product-gallery-inspect__zoom-value" aria-live="polite">100%</span>
        <button type="button" class="product-gallery-inspect__zoom-btn" data-zoom-step="1" aria-label="Zoom in">+</button>
      </div>
      <p class="product-gallery-inspect__hint">Click to zoom in · Right-click to zoom out · Drag to pan when zoomed</p>
    `;

    this.stage = this.overlay.querySelector('.product-gallery-inspect__stage');
    this.image = this.overlay.querySelector('.product-gallery-inspect__image');
    this.zoomCursor = this.overlay.querySelector('.product-gallery-inspect__zoom-cursor');
    this.zoomLabel = this.overlay.querySelector('.product-gallery-inspect__zoom-value');
    this.zoomSlider = this.overlay.querySelector('.product-gallery-inspect__zoom-slider');
    this.image.alt = alt;
    this.image.src = src;

    let lastTap = 0;
    this.image.addEventListener('click', (event) => {
      const now = Date.now();
      if (now - lastTap < 320) {
        this.cancelClickZoom();
        if (this.scale > 1.05) this.setScale(this.minScale);
        else this.setScale(this.maxScale, event.clientX, event.clientY);
        lastTap = 0;
        return;
      }
      lastTap = now;
      this.scheduleClickZoom(event.clientX, event.clientY, this.getClickZoomDirection());
    });

    this.image.addEventListener('contextmenu', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.cancelClickZoom();
      this.scheduleClickZoom(event.clientX, event.clientY, -1, 0);
    });

    this.image.addEventListener('dblclick', (event) => {
      event.preventDefault();
      this.cancelClickZoom();
      if (this.scale > 1.05) this.setScale(this.minScale);
      else this.setScale(this.maxScale, event.clientX, event.clientY);
    });

    this.overlay.querySelectorAll('[data-zoom-step]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        const direction = parseInt(button.dataset.zoomStep, 10);
        const stageRect = this.stage.getBoundingClientRect();
        const originX = stageRect.left + stageRect.width / 2;
        const originY = stageRect.top + stageRect.height / 2;
        this.stepZoomLevel(direction, originX, originY);
      });
    });

    this.zoomSlider.addEventListener('input', (event) => {
      event.stopPropagation();
      const stageRect = this.stage.getBoundingClientRect();
      const originX = stageRect.left + stageRect.width / 2;
      const originY = stageRect.top + stageRect.height / 2;
      this.setScale(parseInt(event.target.value, 10) / 100, originX, originY);
    });

    document.body.appendChild(this.overlay);
    document.body.classList.add('product-gallery-inspect--open');

    this.overlay.querySelector('.product-gallery-inspect__close').addEventListener('click', () => this.close());
    this.overlay.querySelector('.product-gallery-inspect__backdrop').addEventListener('click', () => this.close());

    this.stage.addEventListener('pointerdown', this.onPointerDown);
    this.stage.addEventListener('pointermove', this.onPointerMove);
    this.stage.addEventListener('pointerup', this.onPointerUp);
    this.stage.addEventListener('pointercancel', this.onPointerUp);
    this.stage.addEventListener('wheel', this.onWheel, { passive: false });
    this.stage.addEventListener('pointermove', this.onStagePointerMoveCursor);
    this.stage.addEventListener('pointerenter', this.onStagePointerEnter);
    this.stage.addEventListener('pointerleave', this.onStagePointerLeave);
    this.stage.addEventListener('click', this.onStageClickZoom);
    this.stage.addEventListener('contextmenu', this.onStageContextMenu);

    document.addEventListener('keydown', this.onKeyDown);
    this.updateZoomUI();
    this.updateStageCursorMode();
    requestAnimationFrame(() => this.overlay.classList.add('is-visible'));
  }

  close() {
    if (!this.overlay) return;

    this.stage?.removeEventListener('pointerdown', this.onPointerDown);
    this.stage?.removeEventListener('pointermove', this.onPointerMove);
    this.stage?.removeEventListener('pointerup', this.onPointerUp);
    this.stage?.removeEventListener('pointercancel', this.onPointerUp);
    this.stage?.removeEventListener('wheel', this.onWheel);
    this.stage?.removeEventListener('pointermove', this.onStagePointerMoveCursor);
    this.stage?.removeEventListener('pointerenter', this.onStagePointerEnter);
    this.stage?.removeEventListener('pointerleave', this.onStagePointerLeave);
    this.stage?.removeEventListener('click', this.onStageClickZoom);
    this.stage?.removeEventListener('contextmenu', this.onStageContextMenu);
    document.removeEventListener('keydown', this.onKeyDown);

    this.cancelClickZoom();

    this.overlay.remove();
    this.overlay = null;
    this.image = null;
    this.stage = null;
    this.zoomCursor = null;
    this.zoomLabel = null;
    this.zoomSlider = null;
    this.clickZoomMoved = false;
    this.clickZoomStart = null;
    this.pointers.clear();
    document.body.classList.remove('product-gallery-inspect--open');
    this.resetTransform();
  }

  onKeyDown(event) {
    if (event.key === 'Escape') this.close();
  }

  resetTransform() {
    this.scale = 1;
    this.panX = 0;
    this.panY = 0;
    this.applyTransform();
    this.updateZoomUI();
  }

  updateZoomUI() {
    if (!this.zoomLabel || !this.zoomSlider) return;
    const percent = Math.round(this.scale * 100);
    this.zoomLabel.textContent = `${percent}%`;
    this.zoomSlider.value = String(Math.min(600, Math.max(100, percent)));
  }

  getNearestLevelIndex(scale = this.scale) {
    let nearestIndex = 0;
    let nearestDistance = Infinity;

    this.zoomLevels.forEach((level, index) => {
      const distance = Math.abs(level - scale);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  }

  getClickZoomDirection() {
    if (!this.canUseZoomCursor) {
      if (this.scale <= 1.05) return 1;
      if (this.canZoomOut()) return -1;
    }

    const currentIndex = this.getNearestLevelIndex();
    const atMaxLevel =
      currentIndex === this.zoomLevels.length - 1 &&
      Math.abs(this.scale - this.zoomLevels[currentIndex]) < 0.02;

    if (atMaxLevel) return -1;
    if (this.scale < this.maxScale - 0.001) return 1;
    return -1;
  }

  canZoomIn() {
    return this.scale < this.maxScale - 0.001;
  }

  canZoomOut() {
    return this.scale > this.minScale + 0.001;
  }

  stepZoomLevel(direction, originX, originY) {
    const nextIndex = Math.min(
      this.zoomLevels.length - 1,
      Math.max(0, this.getNearestLevelIndex() + direction)
    );
    this.setScale(this.zoomLevels[nextIndex], originX, originY);
  }

  applyTransform() {
    if (!this.image) return;
    this.image.style.transform = `translate3d(${this.panX}px, ${this.panY}px, 0) scale(${this.scale})`;
  }

  clampPan() {
    if (!this.image || !this.stage) return;

    const stageRect = this.stage.getBoundingClientRect();
    const scaledWidth = this.image.offsetWidth * this.scale;
    const scaledHeight = this.image.offsetHeight * this.scale;
    const maxX = Math.max(0, (scaledWidth - stageRect.width) / 2);
    const maxY = Math.max(0, (scaledHeight - stageRect.height) / 2);

    this.panX = Math.min(maxX, Math.max(-maxX, this.panX));
    this.panY = Math.min(maxY, Math.max(-maxY, this.panY));
  }

  setScale(nextScale, originX, originY) {
    const prevScale = this.scale;
    this.scale = Math.min(this.maxScale, Math.max(this.minScale, nextScale));

    if (this.stage && originX != null && originY != null && prevScale !== this.scale) {
      const stageRect = this.stage.getBoundingClientRect();
      const offsetX = originX - stageRect.left - stageRect.width / 2;
      const offsetY = originY - stageRect.top - stageRect.height / 2;
      const scaleRatio = this.scale / prevScale;
      this.panX = offsetX - (offsetX - this.panX) * scaleRatio;
      this.panY = offsetY - (offsetY - this.panY) * scaleRatio;
    }

    if (this.scale === 1) {
      this.panX = 0;
      this.panY = 0;
    } else {
      this.clampPan();
    }

    this.applyTransform();
    this.updateZoomUI();
    this.updateStageCursorMode();
  }

  updateStageCursorMode() {
    if (!this.stage) return;

    const canZoomIn = this.canUseZoomCursor && this.canZoomIn();
    const canZoomOut = this.canUseZoomCursor && this.canZoomOut();
    const showZoomOut = this.stage.classList.contains('product-gallery-inspect__stage--cursor-zoom-out');

    this.stage.classList.toggle('product-gallery-inspect__stage--can-zoom-in', canZoomIn);
    this.stage.classList.toggle('product-gallery-inspect__stage--can-zoom-out', canZoomOut);
    this.stage.classList.toggle(
      'product-gallery-inspect__stage--can-zoom',
      canZoomIn || canZoomOut
    );

    if (!canZoomIn && !canZoomOut) {
      this.stage.classList.remove('product-gallery-inspect__stage--cursor-visible');
      this.stage.classList.remove('product-gallery-inspect__stage--cursor-zoom-out');
      return;
    }

    if (!showZoomOut && !canZoomIn && canZoomOut) {
      this.stage.classList.add('product-gallery-inspect__stage--cursor-zoom-out');
    }
  }

  updateStageCursorVariant(event) {
    if (!this.stage) return;

    const preferZoomOut =
      (event?.buttons === 2 || event?.altKey) && this.canZoomOut();
    const canZoomIn = this.canZoomIn();
    const canZoomOut = this.canZoomOut();

    this.stage.classList.toggle('product-gallery-inspect__stage--cursor-zoom-out', preferZoomOut);

    if (preferZoomOut || canZoomIn || (canZoomOut && !canZoomIn)) {
      this.stage.classList.add('product-gallery-inspect__stage--cursor-visible');
    } else {
      this.stage.classList.remove('product-gallery-inspect__stage--cursor-visible');
    }
  }

  onStagePointerMoveCursor(event) {
    if (!this.canUseZoomCursor || !this.stage || !this.zoomCursor) return;
    if (event.pointerType !== 'mouse') return;

    const rect = this.stage.getBoundingClientRect();
    this.zoomCursor.style.left = `${event.clientX - rect.left}px`;
    this.zoomCursor.style.top = `${event.clientY - rect.top}px`;
    this.updateStageCursorVariant(event);
  }

  onStagePointerEnter(event) {
    if (!this.canUseZoomCursor || event.pointerType !== 'mouse') return;
    if (!this.stage.classList.contains('product-gallery-inspect__stage--can-zoom')) return;
    this.updateStageCursorVariant(event);
  }

  onStagePointerLeave(event) {
    if (event.pointerType !== 'mouse') return;
    this.stage?.classList.remove('product-gallery-inspect__stage--cursor-visible');
    this.stage?.classList.remove('product-gallery-inspect__stage--cursor-zoom-out');
  }

  scheduleClickZoom(clientX, clientY, direction = 1, delay = 280) {
    if (this.clickZoomMoved) return;
    if (direction > 0 && !this.canZoomIn()) return;
    if (direction < 0 && !this.canZoomOut()) return;

    this.cancelClickZoom();

    if (delay === 0) {
      this.stepZoomLevel(direction, clientX, clientY);
      return;
    }

    this.pendingZoomClick = window.setTimeout(() => {
      this.pendingZoomClick = null;
      if (this.clickZoomMoved) return;
      if (direction > 0 && !this.canZoomIn()) return;
      if (direction < 0 && !this.canZoomOut()) return;
      this.stepZoomLevel(direction, clientX, clientY);
    }, delay);
  }

  cancelClickZoom() {
    if (this.pendingZoomClick) {
      window.clearTimeout(this.pendingZoomClick);
      this.pendingZoomClick = null;
    }
  }

  onStageClickZoom(event) {
    if (event.target === this.image || event.target.closest('.product-gallery-inspect__image')) return;
    this.scheduleClickZoom(event.clientX, event.clientY, this.getClickZoomDirection());
  }

  onStageContextMenu(event) {
    if (event.target === this.image || event.target.closest('.product-gallery-inspect__image')) return;
    event.preventDefault();
    this.cancelClickZoom();
    this.scheduleClickZoom(event.clientX, event.clientY, -1, 0);
  }

  onWheel(event) {
    event.preventDefault();
    this.stepZoomLevel(event.deltaY < 0 ? 1 : -1, event.clientX, event.clientY);
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.clickZoomMoved = false;
    this.clickZoomStart = { x: event.clientX, y: event.clientY };

    this.stage.setPointerCapture(event.pointerId);
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.pointers.size === 2) {
      this.lastPinchDistance = this.getPinchDistance();
      this.isPanning = false;
      return;
    }

    if (this.pointers.size === 1 && this.scale > 1) {
      this.isPanning = true;
      this.panStart = {
        x: event.clientX,
        y: event.clientY,
        panX: this.panX,
        panY: this.panY,
      };
    }
  }

  onPointerMove(event) {
    if (!this.pointers.has(event.pointerId)) return;

    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (
      this.clickZoomStart &&
      this.pointers.size === 1 &&
      Math.hypot(event.clientX - this.clickZoomStart.x, event.clientY - this.clickZoomStart.y) > 8
    ) {
      this.clickZoomMoved = true;
    }

    if (this.pointers.size === 2) {
      const distance = this.getPinchDistance();
      if (this.lastPinchDistance > 0) {
        const center = this.getPinchCenter();
        const ratio = distance / this.lastPinchDistance;
        this.setScale(this.scale * ratio, center.x, center.y);
      }
      this.lastPinchDistance = distance;
      return;
    }

    if (this.isPanning && this.scale > 1) {
      this.panX = this.panStart.panX + (event.clientX - this.panStart.x);
      this.panY = this.panStart.panY + (event.clientY - this.panStart.y);
      this.clampPan();
      this.applyTransform();
    }
  }

  onPointerUp(event) {
    this.pointers.delete(event.pointerId);

    if (this.pointers.size < 2) {
      this.lastPinchDistance = 0;
    }

    if (this.pointers.size === 0) {
      this.isPanning = false;
      try {
        this.stage.releasePointerCapture(event.pointerId);
      } catch (_error) {
        /* pointer may already be released */
      }
    }
  }

  getPinchDistance() {
    const points = [...this.pointers.values()];
    if (points.length < 2) return 0;
    const dx = points[0].x - points[1].x;
    const dy = points[0].y - points[1].y;
    return Math.hypot(dx, dy);
  }

  getPinchCenter() {
    const points = [...this.pointers.values()];
    return {
      x: (points[0].x + points[1].x) / 2,
      y: (points[0].y + points[1].y) / 2,
    };
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productGalleryInspect = new ProductGalleryInspect();
  });
} else {
  window.productGalleryInspect = new ProductGalleryInspect();
}
